import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Search, Trash2, PackageSearch, Printer, ListFilter, Download, ArrowUp, ArrowDown, Users, UserX, RefreshCw, Star, X, MessageCircle } from 'lucide-react';
import {
  OrderSheet,
  Product,
  ProductMutationPayload,
  ProductTemplate,
  SellerProductOrderMetricsBySheet,
  Store,
  Tag,
} from '../../types';
import { ProductEditor } from './ProductEditor';
import { ProductCommentsModal } from './ProductCommentsModal';
import { ProductOrderHistoryModal } from './ProductOrderHistoryModal';
import {
  getAdminProduct,
  createProduct,
  deleteProduct,
  getProductTemplates,
  deleteProductTemplate,
  updateProductTemplate,
} from '../../api/products';
import { updateProductWithStockConfirmation } from '../../utils/productStockReduction';
import { SellerProductSortBy } from '../../api/seller-pages';
// @ts-ignore
import html2pdf from 'html2pdf.js';

import { getAdminTags } from '../../api/tags';

interface ProductsTabProps {
  products: Product[];
  orderMetricsBySheet: SellerProductOrderMetricsBySheet;
  stores: Store[];
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  activeTag: string | null;
  onActiveTagChange: (value: string | null) => void;
  sortBy: SellerProductSortBy;
  onSortByChange: (value: SellerProductSortBy) => void;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onOpenEditor?: (product?: Product) => void;
  onRefresh?: () => Promise<void> | void;
  selectedOrderSheetIds: string[];
  onOrderSheetChange?: (ids: string[]) => void;
  orderSheets?: OrderSheet[];
  onDeleteProduct?: (id: string) => void;
  availableTags?: Tag[];
  onViewCustomerHistory?: (
    name: string,
    phone: string,
    userId?: string,
    orderSheetId?: string,
  ) => void;
}

type ProductHistoryTarget = {
  product: Product;
  option?: {
    id: string;
    name: string;
  };
  cancelled?: boolean;
};

export const ProductsTab: React.FC<ProductsTabProps> = ({
  products,
  orderMetricsBySheet,
  stores,
  searchTerm,
  setSearchTerm,
  activeTag,
  onActiveTagChange,
  sortBy,
  onSortByChange,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  onOpenEditor,
  onRefresh: parentRefresh,
  selectedOrderSheetIds,
  onOrderSheetChange,
  orderSheets = [],
  onDeleteProduct,
  availableTags: initialAvailableTags,
  onViewCustomerHistory,
}) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);
  const [selectedHistoryTarget, setSelectedHistoryTarget] = useState<ProductHistoryTarget | null>(null);
  const [selectedCommentsProduct, setSelectedCommentsProduct] = useState<Product | null>(null);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [loadingEditorProductId, setLoadingEditorProductId] = useState<string | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [productTemplates, setProductTemplates] = useState<ProductTemplate[]>([]);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const latestOrderSheetsByStore = useMemo(() => {
    const latestSheetMap = new Map<string, OrderSheet>();

    [...orderSheets]
      .sort(
        (a, b) =>
          new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime(),
      )
      .forEach((sheet) => {
        if (!latestSheetMap.has(sheet.storeId)) {
          latestSheetMap.set(sheet.storeId, sheet);
        }
      });

    return Array.from(latestSheetMap.values()).sort((a, b) => {
      const storeA = stores.find((store) => store.id === a.storeId);
      const storeB = stores.find((store) => store.id === b.storeId);
      const posA = storeA?.position ?? 999;
      const posB = storeB?.position ?? 999;

      if (posA !== posB) {
        return posA - posB;
      }

      return (
        new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime()
      );
    });
  }, [orderSheets, stores]);

  // Pre-calculate order volumes for all products based on active order sheets
  const productsWithOrderVolume = useMemo(() => {
    const isAllOrderSheetsSelected = selectedOrderSheetIds.includes('all');
    const activeSheetIds = isAllOrderSheetsSelected ? [] : selectedOrderSheetIds;

    const totalOrderedByProduct: Record<string, number> = {};
    const totalOrderedByOption: Record<string, number> = {};

    activeSheetIds.forEach((sheetId) => {
      const metrics = orderMetricsBySheet?.[sheetId];
      if (!metrics) {
        return;
      }

      Object.entries<number>(metrics.productQuantities || {}).forEach(([productId, quantity]) => {
        totalOrderedByProduct[productId] = (totalOrderedByProduct[productId] || 0) + quantity;
      });

      Object.entries<number>(metrics.optionQuantities || {}).forEach(([optionId, quantity]) => {
        totalOrderedByOption[optionId] = (totalOrderedByOption[optionId] || 0) + quantity;
      });
    });

    return (products || []).map(p => {
      const totalStock = Number(p.totalStock || 0);
      const totalOrdered = isAllOrderSheetsSelected
        ? Math.max(0, totalStock - Number(p.availableStock ?? totalStock))
        : totalOrderedByProduct[p.id] || 0;
      const optionsWithOrdered = p.options ? p.options.map((opt: any) => {
        const optionStock = Number(opt.stock || 0);
        const optOrdered = isAllOrderSheetsSelected
          ? Math.max(
              0,
              optionStock - Number(opt.availableStock ?? optionStock),
            )
          : totalOrderedByOption[opt.id] || 0;
        return {
          ...opt,
          optionOrdered: optOrdered,
          optionAvailableStock: Math.max(0, optionStock - optOrdered),
        };
      }) : p.options;
      return {
        ...p,
        totalOrdered,
        displayAvailableStock: Math.max(0, totalStock - totalOrdered),
        options: optionsWithOrdered,
      };
    });
  }, [
    orderMetricsBySheet,
    products,
    selectedOrderSheetIds,
  ]);

  useEffect(() => {
    if (selectedOrderSheetIds.includes('all') || !onOrderSheetChange) {
      return;
    }

    const validSheetIds = new Set(
      latestOrderSheetsByStore.map((sheet) => sheet.id),
    );
    const nextSelectedIds = selectedOrderSheetIds.filter((sheetId) =>
      validSheetIds.has(sheetId),
    );

    if (nextSelectedIds.length === selectedOrderSheetIds.length) {
      return;
    }

    onOrderSheetChange(nextSelectedIds.length > 0 ? nextSelectedIds : ['all']);
  }, [
    latestOrderSheetsByStore,
    onOrderSheetChange,
    selectedOrderSheetIds,
  ]);

  useEffect(() => {
    if (initialAvailableTags) {
      setAvailableTags(initialAvailableTags);
      return;
    }

    getAdminTags().then(setAvailableTags).catch(console.error);
  }, [initialAvailableTags]);

  const onRefresh = () => {
    if (!initialAvailableTags) {
      getAdminTags().then(setAvailableTags).catch(console.error);
    }
    if (parentRefresh) parentRefresh();
  };

  const isOrderVolumeSort =
    sortBy === 'ORDER_VOLUME_ASC' || sortBy === 'ORDER_VOLUME_DESC';
  const isWaitlistSort =
    sortBy === 'WAITLIST_ASC' || sortBy === 'WAITLIST_DESC';
  const handleColumnSort = (
    ascendingSort: SellerProductSortBy,
    descendingSort: SellerProductSortBy,
  ) => {
    onSortByChange(sortBy === ascendingSort ? descendingSort : ascendingSort);
  };
  const renderSortArrows = (
    ascendingSort: SellerProductSortBy,
    descendingSort: SellerProductSortBy,
  ) => (
    <span className="flex flex-col -space-y-1" aria-hidden="true">
      <ArrowUp
        size={11}
        strokeWidth={3}
        className={sortBy === ascendingSort ? 'opacity-100' : 'opacity-25'}
      />
      <ArrowDown
        size={11}
        strokeWidth={3}
        className={sortBy === descendingSort ? 'opacity-100' : 'opacity-25'}
      />
    </span>
  );
  const renderSortableHeader = (
    label: string,
    ascendingSort: SellerProductSortBy,
    descendingSort: SellerProductSortBy,
  ) => {
    const isAscending = sortBy === ascendingSort;
    const isDescending = sortBy === descendingSort;
    const nextDirection = isAscending ? '내림차순' : '오름차순';

    return (
      <button
        type="button"
        onClick={() => handleColumnSort(ascendingSort, descendingSort)}
        className="ml-auto flex items-center justify-end gap-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
        aria-label={`${label} ${nextDirection} 정렬`}
        title={`${label} ${nextDirection} 정렬`}
      >
        <span>{label}</span>
        {renderSortArrows(ascendingSort, descendingSort)}
        <span className="sr-only">
          {isAscending ? '오름차순 정렬됨' : isDescending ? '내림차순 정렬됨' : '정렬 안 됨'}
        </span>
      </button>
    );
  };

  const orderSheetLabelMap = useMemo(() => {
    const storeNameMap = new Map<string, string>(
      (stores || []).map((store) => [store.id, store.name] as const),
    );
    const entries = (orderSheets || []).map((sheet) => {
      const storeName = sheet.storeId
        ? storeNameMap.get(sheet.storeId)?.trim()
        : '';
      const displayMonthDay = sheet.deliveryDate
        ? new Date(sheet.deliveryDate).toLocaleDateString('ko-KR', {
          month: 'numeric',
          day: 'numeric',
        })
        : '';
      const displayText = storeName && sheet.label
        ? `[${storeName}] ${sheet.label}`
        : displayMonthDay || '제목없음';
      return [sheet.id, displayText] as const;
    });

    return new Map(entries);
  }, [orderSheets, stores]);

  const renderLinkedOrderSheets = (product: Product) => {
    if (!Array.isArray(product.orderSheetIds) || product.orderSheetIds.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        {product.orderSheetIds.map((sheetId) => (
          <span
            key={sheetId}
            className="text-[9px] font-black text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded-md"
          >
            {orderSheetLabelMap.get(sheetId) || '연결된 주문서'}
          </span>
        ))}
      </div>
    );
  };


  const handleOpenEditor = async (product?: Product) => {
    setEditingTemplateId(null);

    if (product?.id) {
      try {
        setLoadingEditorProductId(product.id);
        const detailedProduct = await getAdminProduct(product.id);

        if (!detailedProduct) {
          alert('상품 상세 정보를 불러오지 못했습니다.');
          return;
        }

        setEditingProduct(detailedProduct);
      } catch (error) {
        console.error('Failed to load product detail', error);
        alert('상품 상세 정보를 불러오지 못했습니다.');
        return;
      } finally {
        setLoadingEditorProductId(null);
      }
    } else {
      setEditingProduct({
        name: '',
        price: 0,
        totalStock: 0,
        isCustomerCancellationEnabled: true,
        tags: []
      } as any);
    }
    setIsEditorOpen(true);
  };

  const buildProductFromTemplate = (template: ProductTemplate): Partial<Product> => {
    const data = template.data || {};
    const images = data.images && data.images.length > 0
      ? data.images
      : data.image
        ? [data.image]
        : [];

    return {
      name: data.name || template.name,
      shortName: data.shortName || '',
      price: data.price ?? 0,
      image: data.image || images[0] || '',
      images,
      description: data.description || '',
      isClosed: data.isClosed || false,
      isHidden: data.isHidden || false,
      isCommentEnabled: data.isCommentEnabled || false,
      isCustomerCancellationEnabled:
        data.isCustomerCancellationEnabled !== false,
      salesStartDate: data.salesStartDate || undefined,
      salesEndDate: data.salesEndDate || undefined,
      visibleStoreIds: data.visibleStoreIds || [],
      arrivalDateValues: data.arrivalDateValues || [],
      tags: data.tags || [],
      totalStock: data.totalStock ?? 0,
      orderSheetIds: [],
      options: (data.options || []).map((option, index) => ({
        name: option.name,
        price: option.price ?? undefined,
        stock: option.stock ?? 0,
        position: option.position ?? index,
      })) as any,
    };
  };

  const loadProductTemplates = async () => {
    try {
      setIsTemplateLoading(true);
      setProductTemplates(await getProductTemplates());
    } catch (error) {
      console.error('Failed to load product templates', error);
      alert('즐겨찾기 상품을 불러오지 못했습니다.');
    } finally {
      setIsTemplateLoading(false);
    }
  };

  const handleOpenTemplateModal = () => {
    setIsTemplateModalOpen(true);
    void loadProductTemplates();
  };

  const handleUseTemplate = (template: ProductTemplate) => {
    setEditingTemplateId(null);
    setEditingProduct(buildProductFromTemplate(template));
    setIsTemplateModalOpen(false);
    setIsEditorOpen(true);
  };

  const handleEditTemplate = (template: ProductTemplate) => {
    setEditingTemplateId(template.id);
    setEditingProduct(buildProductFromTemplate(template));
    setIsTemplateModalOpen(false);
    setIsEditorOpen(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('이 즐겨찾기 상품을 삭제하시겠습니까?')) return;

    try {
      await deleteProductTemplate(templateId);
      setProductTemplates((current) =>
        current.filter((template) => template.id !== templateId),
      );
    } catch (error) {
      console.error('Failed to delete product template', error);
      alert('즐겨찾기 상품 삭제에 실패했습니다.');
    }
  };

  const handleSaveProduct = async (productData?: ProductMutationPayload) => {
    const templateIdToUpdate = editingTemplateId;

    if (!productData) {
      setIsEditorOpen(false);
      setEditingProduct(null);
      setEditingTemplateId(null);
      if (!templateIdToUpdate) {
        onRefresh();
      }
      return;
    }

    const { isOptionMode, options: rawOptions, ...rest } = productData;
    const { name, price } = rest;

    if (!name || price === undefined) {
      alert('상품명과 판매가를 입력해주세요.');
      return;
    }

    const payload: ProductMutationPayload = {
      ...rest,
      options: isOptionMode && rawOptions && rawOptions.length > 0
        ? rawOptions.map((o: any) => ({ id: o.id, name: o.name, price: o.price ?? null, stock: o.stock || 0 }))
        : []
    };

    try {
      if (templateIdToUpdate) {
        const template = await updateProductTemplate(templateIdToUpdate, {
          ...payload,
          orderSheetIds: [],
        });

        setProductTemplates((current) =>
          current.map((item) => (item.id === template.id ? template : item)),
        );
        alert('즐겨찾기 상품이 수정되었습니다.');
      } else if (payload.id) {
        const updatedProduct = await updateProductWithStockConfirmation(
          payload.id,
          payload,
        );
        if (!updatedProduct) {
          return;
        }
        alert('상품 정보가 수정되었습니다.');
      } else {
        await createProduct(payload);
        alert('신규 상품이 등록되었습니다.');
      }

      setIsEditorOpen(false);
      setEditingProduct(null);
      setEditingTemplateId(null);
      if (!templateIdToUpdate) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to save product', error);
      alert(templateIdToUpdate ? '즐겨찾기 상품 수정에 실패했습니다.' : '상품 저장에 실패했습니다.');
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('정말 이 상품을 삭제하시겠습니까?')) return;
    try {
      if (onDeleteProduct) {
        await onDeleteProduct(id);
      } else {
        await deleteProduct(id);
        alert('상품이 삭제되었습니다.');
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to delete product', error);
      alert('상품 삭제에 실패했습니다.');
    }
  };

  const handleBumpProduct = async (id: string) => {
    if (!window.confirm('이 상품을 최상단으로 끌어올리시겠습니까?')) return;
    try {
      const { bumpProduct } = await import('../../api/products');
      await bumpProduct(id);
      alert('상품이 성공적으로 끌어올려졌습니다.');
      onRefresh();
    } catch (error) {
      console.error('Failed to bump product', error);
      alert('상품 끌어올리기에 실패했습니다.');
    }
  };

  const handleDownloadSummaryPDF = async () => {
    const element = document.querySelector('.bixolon-print-area');
    if (!element) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'bixolon-pdf-export';
    wrapper.appendChild(element.cloneNode(true));
    document.body.appendChild(wrapper);

    const heightInPx = element.scrollHeight || 1000;
    const heightInMm = Math.max((heightInPx * 25.4) / 96 + 10, 100);

    const opt = {
      margin: 0,
      filename: `상품집계표_${new Date().toLocaleDateString('ko-KR')}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2, useCORS: true, logging: false, windowWidth: 300, letterRendering: true },
      jsPDF: { unit: 'mm', format: [72, heightInMm], orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    try {
      await html2pdf().set(opt).from(wrapper).save();
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  return (
    <div className="space-y-4">
      <style dangerouslySetInnerHTML={{
        __html: `
        @media print {
          @page { size: 80mm auto; margin: 0; }
          /* Hide EVERYTHING by default in print */
          body * { visibility: hidden; }
          /* Only show the print area and its items */
          .bixolon-print-area, .bixolon-print-area * { visibility: visible !important; color: black !important; }
          .bixolon-print-area { 
            display: block !important; 
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 72mm !important; 
            margin: 0 !important; 
            padding: 4mm !important; 
            background: white !important; 
          }
          body { visibility: visible !important; overflow: visible !important; background: white !important; }
          .print\\\\:hidden { display: none !important; }
          div[class*="fixed"], div[class*="absolute"], div[class*="relative"] {
            position: static !important; overflow: visible !important; width: auto !important; height: auto !important;
            max-height: none !important; box-shadow: none !important; background: transparent !important;
            padding: 0 !important; margin: 0 !important; transform: none !important;
          }
        }
        @media screen {
          .bixolon-print-area { display: none !important; }
        }
        
        .bixolon-pdf-export {
            display: block !important;
            width: 72mm !important;
            background: white !important;
            margin: 0;
            padding: 0;
            overflow: hidden !important;
        }
        .bixolon-pdf-export .bixolon-print-area {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            color: black !important;
            width: 72mm !important;
            margin: 0 auto !important;
            padding: 2mm !important;
            line-height: 1.4 !important;
            box-sizing: border-box;
        }
      `}} />

      {/* 상품 수량 집계서 (Bixolon 80mm 용지 전용 렌더링 영역 - 화면에선 숨김) */}
      <div className="bixolon-print-area font-sans leading-snug">
        <div className="text-center font-black text-lg mb-2">상품 수량 집계서</div>
        <div className="border-t-[3px] border-black my-2"></div>
        <div className="flex text-xs font-bold leading-none mb-1">
          <div className="w-8">연번</div>
          <div className="flex-1">상품명</div>
          <div className="w-12 text-right">주문량</div>
        </div>
        <div className="border-t border-black border-dashed my-1"></div>
        {productsWithOrderVolume
          .filter((product) => (product as any).totalOrdered > 0)
          .map((item, idx) => (
          <div key={item.id} className="flex text-[11pt] font-medium leading-tight py-0.5 whitespace-nowrap overflow-hidden text-ellipsis">
            <div className="w-8 shrink-0">{idx + 1}.</div>
            <div className="flex-1 overflow-hidden" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
            <div className="w-12 shrink-0 text-right">{item.totalOrdered}</div>
          </div>
        ))}
        <div className="border-t-[3px] border-black my-2 mt-4"></div>
      </div>

      <div className="flex justify-between items-center px-1 print:hidden">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => handleOpenEditor()} className="h-10 px-4 bg-emerald-500 text-white rounded-xl text-xs font-black shadow-lg shadow-emerald-100 flex items-center gap-1.5"><Plus size={16} /> 신규 상품</button>
          <button
            onClick={handleOpenTemplateModal}
            className="h-10 px-4 bg-amber-50 text-amber-600 border border-amber-100 rounded-xl text-xs font-black hover:bg-amber-100 flex items-center gap-1.5"
          >
            <Star size={16} fill="currentColor" /> 즐겨찾기
          </button>
          <button
            onClick={handleDownloadSummaryPDF}
            className="h-10 px-4 bg-gray-900 text-white rounded-xl text-xs font-black shadow-lg shadow-gray-200 flex items-center gap-1.5"
          >
            <Download size={16} /> 집계표 PDF 저장
          </button>
          <button
            onClick={() => window.print()}
            className="h-10 px-4 bg-white border border-gray-200 text-gray-400 rounded-xl text-xs font-black hover:bg-gray-50 flex items-center gap-1.5"
          >
            <Printer size={16} /> 인쇄
          </button>
        </div>
      </div>

      {/* Search and Tag Filters */}
      <div className="flex flex-col gap-4 print:hidden">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="상품명 또는 태그 검색..."
                className="w-full h-12 pl-11 pr-32 bg-white border border-gray-100 rounded-2xl text-[11px] font-bold outline-none focus:border-emerald-500 shadow-sm transition-all"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <button
                onClick={() => setIsAdvancedSearchOpen(!isAdvancedSearchOpen)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg text-[10px] font-black transition-all flex items-center gap-1.5 ${isAdvancedSearchOpen ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
              >
                <ListFilter size={14} /> 고급검색
              </button>
            </div>
            <button
              onClick={() => {
                console.log('%c[REFRESH] Manual Refresh Triggered via UI Button', 'color: #10b981; font-weight: bold;');
                onRefresh?.();
                alert('데이터가 갱신되었습니다.');
              }}
              className="h-12 w-12 flex items-center justify-center bg-white border border-gray-100 rounded-2xl text-gray-400 hover:text-emerald-500 hover:border-emerald-500 shadow-sm transition-all"
              title="데이터 새로고침"
            >
              <RefreshCw size={18} />
            </button>
          </div>

          <div className="flex gap-2 md:hidden">
            <button
              onClick={() => handleColumnSort('ORDER_VOLUME_ASC', 'ORDER_VOLUME_DESC')}
              className={`whitespace-nowrap px-4 py-3 rounded-2xl text-[10px] font-black transition-all flex items-center gap-1.5 ${isOrderVolumeSort ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/10' : 'bg-white text-gray-400 border border-gray-100 hover:border-gray-200'}`}
            >
              {sortBy === 'ORDER_VOLUME_DESC' ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
              {sortBy === 'ORDER_VOLUME_DESC' ? '주문량 높은순' : '주문량 낮은순'}
            </button>
            <button
              onClick={() =>
                onSortByChange(
                  sortBy === 'WAITLIST_DESC' ? 'WAITLIST_ASC' : 'WAITLIST_DESC',
                )
              }
              className={`whitespace-nowrap px-4 py-3 rounded-2xl text-[10px] font-black transition-all flex items-center gap-1.5 ${isWaitlistSort ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/10' : 'bg-white text-gray-400 border border-gray-100 hover:border-gray-200'}`}
            >
              {sortBy === 'WAITLIST_ASC' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              {sortBy === 'WAITLIST_ASC' ? '대기량 낮은순' : '대기량 많은순'}
            </button>
          </div>

          {availableTags.length > 0 && (
            <div className="flex overflow-x-auto no-scrollbar gap-2 md:max-w-md lg:max-w-xl">
              <button
                onClick={() => onActiveTagChange(null)}
                className={`whitespace-nowrap px-4 py-3 rounded-2xl text-[10px] font-black transition-all ${!activeTag ? 'bg-gray-900 text-white shadow-lg' : 'bg-white text-gray-400 border border-gray-100'}`}
              >
                전체 상품
              </button>
              {availableTags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() =>
                    onActiveTagChange(activeTag === tag.name ? null : tag.name)
                  }
                  className={`whitespace-nowrap px-4 py-3 rounded-2xl text-[10px] font-black transition-all ${activeTag === tag.name ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/10' : 'bg-white text-gray-400 border border-gray-100 hover:border-gray-200'}`}
                >
                  #{tag.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Advanced Search Options (Order Sheet Selector) */}
        {isAdvancedSearchOpen && (
          <div className="bg-gray-50/50 p-4 rounded-[28px] border border-dashed animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">주문서 필터</span>
                <button
                  onClick={() => onOrderSheetChange?.(['all'])}
                  className="text-[10px] font-black text-emerald-500 hover:text-emerald-600"
                >
                  필터 초기화
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onOrderSheetChange?.(['all'])}
                  className={`px-3 py-2 rounded-xl text-[10px] font-black transition-all border ${selectedOrderSheetIds.includes('all') ? 'bg-white border-emerald-500 text-emerald-600 shadow-sm' : 'bg-white border-gray-100 text-gray-400 hover:border-gray-200'}`}
                >
                  전체 주문서
                </button>
                {latestOrderSheetsByStore.map(s => {
                  const isSelected = selectedOrderSheetIds.includes(s.id);
                  const displayMonthDay = s.deliveryDate ? new Date(s.deliveryDate).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' }) : '';
                  const displayText = s.label ? `[${s.label}] ${displayMonthDay}` : (displayMonthDay || '제목없음');

                  return (
                    <button
                      key={s.id}
                      onClick={() => {
                        if (selectedOrderSheetIds.includes('all')) {
                          onOrderSheetChange?.([s.id]);
                        } else if (isSelected) {
                          const nextIds = selectedOrderSheetIds.filter(id => id !== s.id);
                          onOrderSheetChange?.(nextIds.length > 0 ? nextIds : ['all']);
                        } else {
                          onOrderSheetChange?.([...selectedOrderSheetIds, s.id]);
                        }
                      }}
                      className={`px-3 py-2 rounded-xl text-[10px] font-black transition-all border ${isSelected ? 'bg-white border-emerald-500 text-emerald-600 shadow-sm' : 'bg-white border-gray-100 text-gray-400 hover:border-gray-200'}`}
                    >
                      {displayText}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-[32px] border shadow-sm overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter w-16">이미지</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">상품명</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-right">단가</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-right">
                {renderSortableHeader('입고량', 'STOCK_ASC', 'STOCK_DESC')}
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-emerald-500/70 uppercase tracking-tighter text-right">
                {renderSortableHeader('주문량', 'ORDER_VOLUME_ASC', 'ORDER_VOLUME_DESC')}
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-amber-500 uppercase tracking-tighter text-right">
                {renderSortableHeader('재고', 'AVAILABLE_STOCK_ASC', 'AVAILABLE_STOCK_DESC')}
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-violet-500 uppercase tracking-tighter text-right">
                {renderSortableHeader('대기', 'WAITLIST_ASC', 'WAITLIST_DESC')}
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-center w-32">관리</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {productsWithOrderVolume.map(item => {
              if (item.options && item.options.length > 0) {
                const group = item;
                const groupTotalStock = group.options.reduce((sum: number, o: any) => sum + (o.stock || 0), 0);
                const groupTotalOrdered = (group as any).totalOrdered || 0;
                const groupAvailableStock = group.options.reduce(
                  (sum: number, o: any) => sum + (o.optionAvailableStock ?? o.availableStock ?? o.stock ?? 0),
                  0,
                );
                return (
                  <React.Fragment key={group.id}>
                    {/* Group Header Row */}
                    <tr className="bg-emerald-50/20 border-b border-emerald-100/50">
                      <td className="px-6 py-4">
                        <div className="w-12 h-12 rounded-xl border bg-gray-50 overflow-hidden shadow-inner">
                          {group.image ? (
                            <img src={group.image} className="w-full h-full object-cover" alt="" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                              <PackageSearch size={16} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4" colSpan={1}>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-extrabold text-emerald-900">{group.name}</span>
                            <span className="text-[9px] font-black text-white bg-emerald-500 px-1.5 py-0.5 rounded-md uppercase tracking-tighter">그룹</span>
                          </div>
                          <div className="text-[10px] font-bold text-emerald-600/70">{group.options.length}개의 옵션 포함</div>
                          {renderLinkedOrderSheets(group)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-[10px] font-bold text-gray-400">그룹 합계</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-black text-gray-900">{groupTotalStock.toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-black text-emerald-500">{groupTotalOrdered.toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4 text-right bg-amber-50/20">
                        <span className="text-sm font-black text-amber-600">{groupAvailableStock.toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4 text-right bg-violet-50/20">
                        <span className={`text-sm font-black ${(group.waitlistCount || 0) > 0 ? 'text-violet-600' : 'text-gray-300'}`}>
                          {(group.waitlistCount || 0).toLocaleString()}개
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-1.5">
                          <button
                            onClick={() => setSelectedCommentsProduct(group)}
                            className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-lg"
                            title="댓글 관리"
                          >
                            <MessageCircle size={14} />
                          </button>
                          <button
                            onClick={() => handleBumpProduct(group.id)}
                            className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-lg"
                            title="끌어올리기"
                          >
                            <ArrowUp size={14} />
                          </button>
                          <button
                            onClick={() => void handleOpenEditor(group)}
                            disabled={loadingEditorProductId === group.id}
                            className="p-1.5 text-gray-400 hover:bg-gray-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDeleteProduct(group.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>

                    {/* Option Sub-rows */}
                    {group.options.map((opt: any) => (
                      <tr key={opt.id} className="hover:bg-gray-50/50 transition-colors group border-b border-gray-50 last:border-b-0">
                        <td className="px-6 py-3 pl-12 relative">
                          <div className="absolute left-8 top-1/2 -translate-y-1/2 w-4 h-4 border-l-2 border-b-2 border-emerald-200 rounded-bl-lg"></div>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-700">{opt.name}</span>
                            {opt.isClosed && (
                              <span className="text-[8px] font-black text-red-600 bg-red-50 px-1 py-0.5 rounded">마감</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <span className="text-xs font-black text-gray-900">{(opt.price ?? group.price).toLocaleString()}원</span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <span className="text-xs font-bold text-gray-500">{opt.stock ?? 0}</span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          {(opt.optionOrdered ?? 0) > 0
                            ? <span className="text-xs font-bold text-emerald-500">{opt.optionOrdered}</span>
                            : <span className="text-xs font-bold text-emerald-500/30">—</span>
                          }
                        </td>
                        <td className="px-6 py-3 text-right">
                          <span className="text-xs font-bold text-amber-500/70">{opt.optionAvailableStock ?? opt.availableStock ?? opt.stock ?? 0}</span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <span className={`text-xs font-bold ${(opt.waitlistCount || 0) > 0 ? 'text-violet-600' : 'text-gray-300'}`}>
                            {(opt.waitlistCount || 0).toLocaleString()}개
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex justify-center gap-1.5 opacity-50 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() =>
                                setSelectedHistoryTarget({
                                  product: group,
                                  option: { id: opt.id, name: opt.name },
                                })
                              }
                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                              title="주문 고객"
                            >
                              <Users size={14} />
                            </button>
                            <button
                              onClick={() =>
                                setSelectedHistoryTarget({
                                  product: group,
                                  option: { id: opt.id, name: opt.name },
                                  cancelled: true,
                                })
                              }
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                              title="취소 고객"
                            >
                              <UserX size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              }

              const p = item;
              return (
                <tr key={p.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="w-12 h-12 rounded-xl border bg-gray-50 overflow-hidden shadow-inner">
                      {p.image ? (
                        <img src={p.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <PackageSearch size={16} />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-black text-gray-900">{p.name}</span>
                        {p.isClosed && (
                          <span className="text-[9px] font-black text-red-600 bg-red-50 px-1.5 py-0.5 rounded-md">판매중지</span>
                        )}
                      </div>
                      {Array.isArray(p.tags) && p.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {p.tags.map((t: string) => (
                            <span key={t} className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">#{t}</span>
                          ))}
                        </div>
                      )}
                      {renderLinkedOrderSheets(p)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-black text-gray-900">{p.price.toLocaleString()}원</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-black text-gray-900">{p.totalStock}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={`text-sm font-black ${isOrderVolumeSort ? 'text-emerald-500' : 'text-gray-900'}`}>{p.totalOrdered}</span>
                  </td>
                  <td className="px-6 py-4 text-right bg-amber-50/30">
                    <span className="text-sm font-black text-amber-600">{(p.displayAvailableStock ?? Math.max(0, p.totalStock - p.totalOrdered)).toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 text-right bg-violet-50/20">
                    <span className={`text-sm font-black ${(p.waitlistCount || 0) > 0 ? 'text-violet-600' : 'text-gray-300'}`}>
                      {(p.waitlistCount || 0).toLocaleString()}개
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => setSelectedHistoryTarget({ product: p })}
                        className="p-2 bg-blue-50 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-xl transition-all"
                        title="주문 내역"
                      >
                        <Users size={16} />
                      </button>
                      <button
                        onClick={() => setSelectedHistoryTarget({ product: p, cancelled: true })}
                        className="p-2 bg-red-50 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-xl transition-all"
                        title="취소 고객"
                      >
                        <UserX size={16} />
                      </button>
                      <button
                        onClick={() => setSelectedCommentsProduct(p)}
                        className="p-2 bg-emerald-50 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 rounded-xl transition-all"
                        title="댓글 관리"
                      >
                        <MessageCircle size={16} />
                      </button>
                      <button
                        onClick={() => handleBumpProduct(p.id)}
                        className="p-2 bg-emerald-50 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 rounded-xl transition-all"
                        title="끌어올리기"
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        onClick={() => void handleOpenEditor(p)}
                        disabled={loadingEditorProductId === p.id}
                        className="p-2 bg-gray-50 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="수정"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(p.id)}
                        className="p-2 bg-red-50 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-xl transition-all"
                        title="삭제"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden bg-white rounded-[24px] border shadow-sm overflow-hidden print:hidden">
        <div className="divide-y divide-gray-50">
          {productsWithOrderVolume.map(item => {
            if (item.options && item.options.length > 0) {
              const group = item;
              const groupTotalStock = group.options.reduce((sum: number, o: any) => sum + (o.stock || 0), 0);
              const groupTotalOrdered = (group as any).totalOrdered || 0;
              const groupAvailableStock = group.options.reduce(
                (sum: number, o: any) => sum + (o.availableStock ?? o.stock ?? 0),
                0,
              );
              return (
                <div key={group.id} className="p-5 space-y-4 bg-emerald-50/10">
                  <div className="flex gap-4">
                    <div className="w-16 h-16 rounded-2xl border bg-white overflow-hidden shadow-sm shrink-0">
                      {group.image ? (
                        <img src={group.image} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <PackageSearch size={18} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-black text-emerald-900 truncate">{group.name}</span>
                        <span className="text-[8px] font-black text-white bg-emerald-500 px-1 py-0.5 rounded uppercase tracking-tighter">그룹</span>
                      </div>
                      {renderLinkedOrderSheets(group)}
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-gray-400 border-b border-gray-100">총 입고</span>
                          <span className="text-xs font-black text-gray-900">{groupTotalStock}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-gray-400 border-b border-gray-100">총 주문</span>
                          <span className="text-xs font-black text-emerald-600">{groupTotalOrdered}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-gray-400 border-b border-gray-100">총 재고</span>
                          <span className="text-xs font-black text-amber-600">{groupAvailableStock}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-gray-400 border-b border-gray-100">대기</span>
                          <span className={`text-xs font-black ${(group.waitlistCount || 0) > 0 ? 'text-violet-600' : 'text-gray-300'}`}>
                            {group.waitlistCount || 0}개
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 self-start">
                      <button
                        onClick={() => setSelectedCommentsProduct(group)}
                        className="p-1.5 bg-emerald-50 text-emerald-500 rounded-lg"
                        title="댓글 관리"
                      >
                        <MessageCircle size={12} />
                      </button>
                      <button
                        onClick={() => handleBumpProduct(group.id)}
                        className="p-1.5 bg-emerald-50 text-emerald-500 rounded-lg"
                        title="끌어올리기"
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        onClick={() => void handleOpenEditor(group)}
                        disabled={loadingEditorProductId === group.id}
                        className="p-1.5 bg-gray-50 text-gray-400 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => handleDeleteProduct(group.id)} className="p-1.5 bg-red-50 text-red-400 rounded-lg"><Trash2 size={12} /></button>
                    </div>
                  </div>

                  {/* Mobile Option List */}
                  <div className="space-y-2 pl-4 border-l-2 border-emerald-100/50">
                    {group.options.map((opt: any) => (
                      <div key={opt.id} className="bg-white p-3 rounded-2xl border border-emerald-100/30 shadow-sm flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1 text-[11px] font-bold text-gray-700">
                            <span className="truncate">{opt.name}</span>
                            {opt.isClosed && <span className="text-[8px] font-black text-red-500">마감</span>}
                          </div>
                          <div className="flex flex-wrap gap-3 text-[10px] font-bold">
                            <span className="text-gray-900">{(opt.price ?? group.price).toLocaleString()}원</span>
                            <span className="text-gray-500">입고 {opt.stock ?? 0}</span>
                            <span className="text-emerald-600">주문 {opt.optionOrdered ?? 0}</span>
                            <span className={`text-amber-600 ${(opt.optionAvailableStock ?? opt.availableStock ?? opt.stock ?? 0) <= 0 ? 'text-red-500' : ''}`}>재고 {opt.optionAvailableStock ?? opt.availableStock ?? opt.stock ?? 0}</span>
                            <span className={(opt.waitlistCount || 0) > 0 ? 'text-violet-600' : 'text-gray-300'}>대기 {opt.waitlistCount || 0}개</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() =>
                              setSelectedHistoryTarget({
                                product: group,
                                option: { id: opt.id, name: opt.name },
                              })
                            }
                            className="p-1.5 bg-blue-50 text-blue-500 rounded-lg"
                            title="주문 고객"
                          >
                            <Users size={12} />
                          </button>
                          <button
                            onClick={() =>
                              setSelectedHistoryTarget({
                                product: group,
                                option: { id: opt.id, name: opt.name },
                                cancelled: true,
                              })
                            }
                            className="p-1.5 bg-red-50 text-red-500 rounded-lg"
                            title="취소 고객"
                          >
                            <UserX size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            const p = item;
            return (
              <div key={p.id} className="p-4 flex gap-4 hover:bg-gray-50/50 transition-colors group">
                <div className="w-16 h-16 rounded-xl border bg-gray-50 overflow-hidden shadow-inner shrink-0 self-center">
                  {p.image ? (
                    <img src={p.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <PackageSearch size={18} />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 truncate">
                      <span className="text-sm font-black text-gray-900 truncate">{p.name}</span>
                      {p.isClosed && (
                        <span className="text-[8px] font-black text-red-600 bg-red-50 px-1 py-0.5 rounded shrink-0">판매중지</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setSelectedHistoryTarget({ product: p })}
                        className="p-1.5 bg-blue-50 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-all"
                        title="주문 내역"
                      >
                        <Users size={12} />
                      </button>
                      <button
                        onClick={() => setSelectedHistoryTarget({ product: p, cancelled: true })}
                        className="p-1.5 bg-red-50 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"
                        title="취소 고객"
                      >
                        <UserX size={12} />
                      </button>
                      <button
                        onClick={() => setSelectedCommentsProduct(p)}
                        className="p-1.5 bg-emerald-50 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 rounded-lg transition-all"
                        title="댓글 관리"
                      >
                        <MessageCircle size={12} />
                      </button>
                      <button
                        onClick={() => handleBumpProduct(p.id)}
                        className="p-1.5 bg-emerald-50 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 rounded-lg transition-all"
                        title="끌어올리기"
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        onClick={() => void handleOpenEditor(p)}
                        disabled={loadingEditorProductId === p.id}
                        className="p-1.5 bg-gray-50 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(p.id)}
                        className="p-1.5 bg-red-50 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-all"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter block leading-none mb-1">단가</span>
                      <span className="text-xs font-black text-gray-900">{p.price.toLocaleString()}원</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter block leading-none mb-1">입고</span>
                      <span className="text-xs font-black text-gray-900">{p.totalStock}</span>
                    </div>
                    <div className="text-right">
                      <span className={`text-[10px] font-bold ${isOrderVolumeSort ? 'text-emerald-500' : 'text-gray-400'} uppercase tracking-tighter block leading-none mb-1`}>주문</span>
                      <span className={`text-xs font-black ${isOrderVolumeSort ? 'text-emerald-500' : 'text-emerald-500/70'}`}>{p.totalOrdered}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-bold text-amber-500 uppercase tracking-tighter block leading-none mb-1">재고</span>
                      <span className="text-xs font-black text-amber-600">{(p.displayAvailableStock ?? Math.max(0, p.totalStock - p.totalOrdered)).toLocaleString()}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-bold text-violet-500 uppercase tracking-tighter block leading-none mb-1">대기</span>
                      <span className={`text-xs font-black ${(p.waitlistCount || 0) > 0 ? 'text-violet-600' : 'text-gray-300'}`}>{p.waitlistCount || 0}개</span>
                    </div>
                  </div>

                  {Array.isArray(p.tags) && p.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {p.tags.map(t => (
                        <span key={t} className="text-[8px] font-black text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded">#{t}</span>
                      ))}
                    </div>
                  )}
                  {renderLinkedOrderSheets(p)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(totalPages > 1 || productsWithOrderVolume.length > 0) && (
        <div className="flex flex-col items-center gap-3 mt-6 pb-6 print:hidden">
          <div className="text-[11px] font-bold text-gray-400">
            총 {totalCount.toLocaleString()}개 중 현재 페이지 {productsWithOrderVolume.length}개 표시
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border text-xs font-bold bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                이전
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 2,
                  )
                  .map((page, index, arr) => (
                    <React.Fragment key={page}>
                      {index > 0 && arr[index - 1] !== page - 1 ? (
                        <span className="text-gray-400 text-xs px-1">...</span>
                      ) : null}
                      <button
                        onClick={() => onPageChange(page)}
                        className={`min-w-[32px] h-8 rounded-lg text-xs font-bold transition-colors ${
                          currentPage === page
                            ? 'bg-gray-900 text-white shadow-sm'
                            : 'bg-white border text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg border text-xs font-bold bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                다음
              </button>
            </div>
          )}
        </div>
      )}

      {
        productsWithOrderVolume.length === 0 && (
          <div className="py-20 text-center bg-white rounded-[32px] border border-dashed border-gray-200">
            <PackageSearch size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-bold">등록된 상품이 없거나 검색 결과가 없습니다.</p>
            <p className="text-gray-400 text-xs mt-1">상단 '신규 상품' 버튼을 통해 상품을 등록해주세요.</p>
          </div>
        )
      }

      {
        isTemplateModalOpen && (
          <div className="fixed inset-0 z-[9100] flex items-end justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsTemplateModalOpen(false)}
            />
            <div className="relative w-full max-w-md max-h-[76vh] bg-white rounded-t-[32px] p-6 shadow-2xl flex flex-col gap-5 animate-in slide-in-from-bottom">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-black text-gray-900">즐겨찾기 상품</h3>
                  <p className="text-[10px] font-bold text-gray-400 mt-0.5">
                    {productTemplates.length.toLocaleString()}개 템플릿
                  </p>
                </div>
                <button
                  onClick={() => setIsTemplateModalOpen(false)}
                  className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto no-scrollbar space-y-2 pr-1">
                {isTemplateLoading ? (
                  <div className="py-12 text-center text-sm font-bold text-gray-400">
                    불러오는 중입니다...
                  </div>
                ) : productTemplates.length > 0 ? (
                  productTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="flex items-center gap-2 rounded-2xl border border-gray-100 bg-gray-50/70 p-2"
                    >
                      <button
                        onClick={() => handleUseTemplate(template)}
                        className="flex-1 min-w-0 text-left bg-white rounded-xl px-4 py-3 border border-transparent hover:border-amber-200 hover:bg-amber-50 transition-all"
                      >
                        <p className="text-sm font-black text-gray-900 truncate">
                          {template.name}
                        </p>
                        <p className="mt-0.5 text-[10px] font-bold text-gray-400">
                          {new Date(template.updatedAt).toLocaleDateString('ko-KR')}
                        </p>
                      </button>
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="h-11 w-11 shrink-0 rounded-xl bg-white text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                        title="즐겨찾기 수정"
                      >
                        <Edit2 size={15} className="mx-auto" />
                      </button>
                      <button
                        onClick={() => void handleDeleteTemplate(template.id)}
                        className="h-11 w-11 shrink-0 rounded-xl bg-white text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="즐겨찾기 삭제"
                      >
                        <Trash2 size={15} className="mx-auto" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="py-12 text-center rounded-2xl border border-dashed border-gray-200">
                    <Star size={28} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm font-bold text-gray-400">
                      저장된 즐겨찾기 상품이 없습니다.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {
        isEditorOpen && editingProduct && (
          <ProductEditor
            editingProduct={editingProduct}
            setEditingProduct={setEditingProduct}
            onSave={handleSaveProduct}
            onClose={() => {
              setIsEditorOpen(false);
              setEditingTemplateId(null);
            }}
            stores={stores}
            availableTags={availableTags}
            orderSheets={orderSheets}
            title={editingTemplateId ? '즐겨찾기 수정' : '상품 설정'}
            submitLabel={editingTemplateId ? '템플릿 저장' : '저장하기'}
            hideTemplateActions={Boolean(editingTemplateId)}
            hideOrderSheetSelector={Boolean(editingTemplateId)}
          />
        )
      }

      {
        selectedHistoryTarget && (
          <ProductOrderHistoryModal
            isOpen={!!selectedHistoryTarget}
            onClose={() => setSelectedHistoryTarget(null)}
            product={selectedHistoryTarget.product}
            option={selectedHistoryTarget.option}
            selectedOrderSheetIds={selectedOrderSheetIds}
            orderSheets={orderSheets}
            onRefresh={onRefresh}
            onViewCustomerHistory={
              onViewCustomerHistory
                ? (name, phone, userId, orderSheetId) => {
                    setSelectedHistoryTarget(null);
                    onViewCustomerHistory(name, phone, userId, orderSheetId);
                  }
                : undefined
            }
            cancelled={selectedHistoryTarget.cancelled}
          />
        )
      }

      {
        selectedCommentsProduct && (
          <ProductCommentsModal
            isOpen={!!selectedCommentsProduct}
            onClose={() => setSelectedCommentsProduct(null)}
            product={selectedCommentsProduct}
          />
        )
      }
    </div >
  );
};
